import React, { useContext, useState } from 'react';
import {
  Grid,
  makeStyles,
  createStyles,
  Theme,
  Typography,
  Paper,
  Box,
  Button,
  Divider,
} from '@material-ui/core';
import clsx from 'clsx';
import colors from '../../../../themes/main-colors';
import { ToggleSwitch } from '../common/toggle-switch';
import OutageMarkerIcon from '../icons/OutageMarkerIcon';
import PlannedOutageIcon from '../icons/PlannedOutageIcon';
import StandardMap from '../../../../images/standard_map.png';
import MonochromeMap from '../../../../images/monochrome_map.png';
import HybridMap from '../../../../images/hybrid_map.png';
import SatelliteMap from '../../../../images/satellite_map.png';
import AreaIcon from '../icons/AreaIcon';
import { useIsMobile } from '../../../../util/style-utils';
import {
  MapType,
  OutageToggles,
  ShowByType,
  useOutageMapOptions,
} from '../../providers/OutageMapOptionsProvider';
import { useTranslation } from '../../../../hooks/useTranslation';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import { useOutageMapContext } from '../../providers/OutageMapProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    gridItem: {
      padding: theme.spacing(3, 2.5),
    },
    gridItemBorder: {
      borderBottom: '2px solid #d5ebf1',
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: 16,
    },
    subtitle: {
      fontSize: 15,
    },
    box: {
      display: 'flex',
      width: '100%',
      borderRadius: '9999px',
      padding: 2,
      backgroundColor: theme.palette.primary.main,
    },
    boxContainer: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.white,
      padding: 20,
      boxShadow: 'none',
      borderRadius: 0,
    },
    tab: {
      flex: 1,
      cursor: 'pointer',
      padding: '2px 4px',
      borderRadius: '9999px',
      backgroundColor: 'transparent',
      color: colors.white,
      fontSize: 15,
      transition: 'all 0.2s ease',
      '&:first-child': {
        borderTopLeftRadius: '9999px',
        borderBottomLeftRadius: '9999px',
      },
      '&:last-child': {
        borderTopRightRadius: '9999px',
        borderBottomRightRadius: '9999px',
      },
    },
    active: {
      backgroundColor: colors.white,
      color: colors.noirBlur,
      boxShadow: theme.shadows[1],
      '&:hover': {
        backgroundColor: colors.white,
      },
    },
    outageTypeDivider: {
      margin: theme.spacing(1.5, 0),
    },
    row: {
      gap: '10px',
      '& .MuiGrid-item': {
        display: 'flex',
      },
    },
    icon: {
      width: '32px',
      height: '32px',
    },
    gap: {
      gap: '20px',
    },
    mapType: {
      gap: '8px',
      width: 'unset',
    },
    mapImage: {
      borderRadius: 10,
      cursor: 'pointer',
    },
    mapImageBorder: {
      border: `2px solid ${colors.starlightBlue}`,
    },
    mapTitle: {
      fontSize: 11,
    },
  }),
);

export default function MapOptions() {
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    showBy,
    setShowBy,
    outageToggles,
    setOutageToggles,
    mapStyle,
    setMapStyle,
  } = useOutageMapOptions();
  const { resetView } = useOutageMapContext();

  const { isOutagesPSPSEnabled, loading } = useContext(featureFlagsContext);

  const tabs = [
    {
      label: t('ALL_SERVICE_AREA'),
      type: ShowByType.DEFAULT,
    },
    {
      label: t('COUNTY'),
      type: ShowByType.COUNTY,
    },
    {
      label: t('ZIP_CODE'),
      type: ShowByType.ZIP,
    },
  ];

  const outageTypes = [
    {
      key: 'unplanned',
      label: t('UNPLANNED_OUTAGE'),
      icon: (
        <OutageMarkerIcon
          text="#"
          className={classes.icon}
          fillColor={colors.roseRed}
          size={32}
          strokeColor={colors.white}
          textColor={colors.white}
        />
      ),
    },
    {
      key: 'planned',
      label: t('PLANNED_OUTAGE'),
      icon: <PlannedOutageIcon className={classes.icon} />,
    },
    ...(!loading && isOutagesPSPSEnabled
      ? [
          {
            key: 'psps',
            label: t('PUBLIC_SAFETY_POWER_SHUTOFF'),
            icon: <AreaIcon className={classes.icon} />,
          },
        ]
      : []),
  ];

  const mapList = [
    { type: MapType.STANDARD, img: StandardMap, title: t('STANDARD') },
    { type: MapType.MONOCHROME, img: MonochromeMap, title: t('MONOCHROME') },
    { type: MapType.HYBRID, img: HybridMap, title: t('HYBRID') },
    { type: MapType.SATELLITE, img: SatelliteMap, title: t('SATELLITE') },
  ];

  return (
    <Grid container direction="column" className={classes.root}>
      <Grid item className={clsx(classes.gridItem, classes.gridItemBorder)}>
        <Typography className={classes.sectionTitle}>{t('SHOW_BY')}</Typography>
        <Box className={classes.box}>
          {tabs.map((tab, idx) => (
            <Button
              key={idx}
              disableRipple
              onClick={() => {
                setShowBy(tab.type);
                resetView();
              }}
              className={clsx(classes.tab, {
                [classes.active]: showBy === tab.type,
              })}
            >
              {tab.label}
            </Button>
          ))}
        </Box>
      </Grid>

      <Grid item className={classes.gridItem}>
        <Typography className={classes.sectionTitle}>
          {t('SHOW_OUTAGE_TYPES')}
        </Typography>
        <Paper className={classes.boxContainer}>
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <Typography variant="subtitle1" className={classes.subtitle}>
                {t('SHOW_ALL')}
              </Typography>
            </Grid>
            <Grid item>
              <ToggleSwitch
                checked={Object.values(outageToggles).every(Boolean)}
                onChange={() => {
                  setOutageToggles(prev => {
                    const allOn = Object.values(prev).every(Boolean);

                    return (Object.keys(
                      prev,
                    ) as (keyof OutageToggles)[]).reduce((acc, key) => {
                      acc[key] = !allOn;
                      return acc;
                    }, {} as OutageToggles);
                  });
                }}
              />
            </Grid>
          </Grid>

          {outageTypes.map((type, idx) => (
            <React.Fragment key={type.key}>
              <Divider className={classes.outageTypeDivider} />
              <Grid
                container
                justify="space-between"
                alignItems="center"
                wrap="nowrap"
              >
                <Grid
                  container
                  item
                  alignItems="center"
                  className={classes.row}
                >
                  <Grid item>{type.icon}</Grid>
                  <Grid item>
                    <Typography
                      variant="subtitle1"
                      className={classes.subtitle}
                    >
                      {type.label}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item>
                  <ToggleSwitch
                    checked={
                      outageToggles[type.key as keyof typeof outageToggles]
                    }
                    onChange={() =>
                      setOutageToggles(prev => ({
                        ...prev,
                        [type.key]: !prev[
                          type.key as keyof typeof outageToggles
                        ],
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
        </Paper>
      </Grid>

      <Grid item className={classes.gridItem}>
        <Typography className={classes.sectionTitle}>
          {t('MAP_STYLE')}
        </Typography>
        <Paper className={clsx(classes.boxContainer, classes.gap)}>
          <Grid container direction="row" justify="space-around">
            {mapList.map(map => (
              <Grid
                key={map.type}
                item
                container
                direction="column"
                alignItems="center"
                className={classes.mapType}
              >
                <Grid item>
                  <img
                    src={map.img}
                    width={isMobile ? 80 : 100}
                    height={isMobile ? 80 : 100}
                    className={clsx(
                      classes.mapImage,
                      mapStyle === map.type && classes.mapImageBorder,
                    )}
                    onClick={() => setMapStyle(map.type)}
                  />
                </Grid>
                <Grid item>
                  <Typography variant="subtitle1" className={classes.mapTitle}>
                    {map.title}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}
